import React, { useState, useContext, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import CommonLayout from '../../_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import { neutral, warning } from '_lex/_foundations/phxColors';
import { AppContext } from '../../../context';
import type { AccreditationVersion } from '../_types/settings';
import useAccreditationVersionsAvailable from '../_data_sources/Settings/useAccreditationVersionsAvailable';
import useAcceptAccreditationVersion from '../_data_sources/Settings/useAcceptAccreditationVersion';
import useRejectAccreditationVersion from '../_data_sources/Settings/useRejectAccreditationVersion';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse, handleError } from 'data_sources/constants';

export default function Settings() {
  const { currentOrganizationId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [versions, setVersions] = useState<AccreditationVersion[]>([]);
  const [originalVersions, setOriginalVersions] = useState<AccreditationVersion[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch accreditation versions
  const {
    data: versionsData,
    isLoading,
    error,
  } = useAccreditationVersionsAvailable(currentOrganizationId || '', {}, (error: any) => {
    console.error('Error fetching accreditation versions:', error);
  });

  const acceptVersionMutation = useAcceptAccreditationVersion();
  const rejectVersionMutation = useRejectAccreditationVersion();

  // Initialize versions when data is loaded
  useEffect(() => {
    if (versionsData?.content) {
      setVersions(versionsData.content);
      setOriginalVersions(versionsData.content);
    }
  }, [versionsData]);

  const handleVersionToggle = (versionId: string) => {
    setVersions((prev) =>
      prev.map((version) =>
        version.versionId === versionId
          ? { ...version, accepted: version.accepted === 'ENABLED' ? 'DISABLED' : 'ENABLED' }
          : version
      )
    );
    setHasChanges(true);
  };

  const handleCancel = () => {
    // Reset to original state
    setVersions(originalVersions);
    setHasChanges(false);
  };

  // Get versions that will be disabled
  const versionsToDisable = useMemo(() => {
    return versions.filter((currentVersion) => {
      const originalVersion = originalVersions.find(
        (v: AccreditationVersion) => v.versionId === currentVersion.versionId
      );
      return originalVersion && originalVersion.accepted === 'ENABLED' && currentVersion.accepted === 'DISABLED';
    });
  }, [versions, originalVersions]);

  const handleSaveClick = async () => {
    // If there are versions to disable, show confirmation dialog
    if (versionsToDisable.length > 0) {
      try {
        await confirm({
          title: t('awb.settings.disableConfirmModal.title'),
          confirmationText: t('awb.settings.disableConfirmModal.confirm'),
          cancellationText: t('awb.settings.disableConfirmModal.cancel'),
          confirmationButtonProps: { color: 'error', variant: 'contained' },
          cancellationButtonProps: { color: 'neutral', variant: 'outlined' },
          content: (
            <Box sx={{ maxHeight: 400 }}>
              <Typography
                variant='body2'
                sx={{ mb: 2 }}
              >
                {t('awb.settings.disableConfirmModal.description')}
              </Typography>
              <Stack spacing={2}>
                {versionsToDisable.map((v) => (
                  <Box
                    key={v.versionId}
                    sx={{
                      border: `1px solid ${neutral[200]}`,
                      borderRadius: 2,
                      p: 3,
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{ fontWeight: 500 }}
                    >
                      {v.version}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: neutral[600] }}
                    >
                      {v.accreditationBodyName}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ),
        });
        await handleSave();
      } catch {
        // User cancelled, do nothing
      }
    } else {
      await handleSave();
    }
  };

  const handleSave = async () => {
    if (!currentOrganizationId) return;

    try {
      // Compare current versions with original versions and execute mutations
      const mutations = [];

      for (const currentVersion of versions) {
        const originalVersion = originalVersions.find(
          (v: AccreditationVersion) => v.versionId === currentVersion.versionId
        );

        if (originalVersion && currentVersion.accepted !== originalVersion.accepted) {
          if (currentVersion.accepted === 'ENABLED') {
            // Need to accept this version
            mutations.push(
              acceptVersionMutation.mutateAsync({
                organizationId: currentOrganizationId,
                accreditationBodyId: currentVersion.accreditationBodyId,
                versionId: currentVersion.versionId,
              })
            );
          } else {
            // Need to reject this version
            mutations.push(
              rejectVersionMutation.mutateAsync({
                organizationId: currentOrganizationId,
                accreditationBodyId: currentVersion.accreditationBodyId,
                versionId: currentVersion.versionId,
              })
            );
          }
        }
      }

      // Execute all mutations
      if (mutations.length > 0) {
        await Promise.all(mutations);

        const queryKey = ['accreditationVersionsAvailable', currentOrganizationId || ''];
        await queryClient.refetchQueries(queryKey);
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      handleError(error as Error, {
        onError: (serverError: ServerErrorResponse) => {
          errorHelper.enqueueErrors(serverError);
        },
      });
    }
  };

  const sortedVersions = [...versions].sort((a, b) => {
    if (a.accreditationBodyName !== b.accreditationBodyName) {
      return a.accreditationBodyName.localeCompare(b.accreditationBodyName);
    }
    return a.version.localeCompare(b.version);
  });

  // Check if any mutation is loading (for save button)
  const isSaving = acceptVersionMutation.isLoading || rejectVersionMutation.isLoading;

  // Show loading state
  if (isLoading) {
    return (
      <CommonLayout
        pageTitle={t('awb.settings.pageTitle')}
        headerProps={{
          title: t('awb.settings.title'),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </CommonLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <CommonLayout
        pageTitle={t('awb.settings.pageTitle')}
        headerProps={{
          title: t('awb.settings.title'),
        }}
      >
        <Box sx={{ p: 4 }}>
          <Alert severity='error'>{t('common.errors.loadingData', 'Error loading data. Please try again.')}</Alert>
        </Box>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout
      pageTitle={t('awb.settings.pageTitle')}
      headerProps={{
        title: t('awb.settings.title'),
        actions: (
          <Stack
            direction='row'
            spacing={2}
          >
            <Button
              variant='outlined'
              onClick={handleCancel}
              disabled={!hasChanges || isSaving}
            >
              {t('common.actions.cancel', 'Cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleSaveClick}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
              loadingPosition='end'
            >
              {t('common.actions.save', 'Save')}
            </Button>
          </Stack>
        ),
      }}
    >
      <Box sx={{ p: 4, maxWidth: 800 }}>
        {/* Accreditation Versions Section */}
        <Box>
          <Typography
            variant='h6'
            sx={{ mb: 1, fontWeight: 600 }}
          >
            {t('awb.settings.accreditationVersions.title')}
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 3, color: neutral[600] }}
          >
            {t('awb.settings.accreditationVersions.description')}
          </Typography>

          {/* Warning Alert */}
          <Alert
            severity='warning'
            icon={<Info />}
            sx={{
              mb: 3,
              backgroundColor: warning[50],
              borderColor: warning[200],
              '& .MuiAlert-icon': {
                color: warning[600],
              },
            }}
          >
            <Typography variant='body2'>{t('awb.settings.accreditationVersions.warning')}</Typography>
          </Alert>

          {/* Versions List */}
          <Stack spacing={2}>
            {sortedVersions.map((version) => (
              <Box
                key={version.versionId}
                sx={{
                  border: `1px solid ${neutral[200]}`,
                  borderRadius: 2,
                  p: 3,
                  backgroundColor: version.accepted === 'ENABLED' ? neutral[50] : 'transparent',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={version.accepted === 'ENABLED'}
                      onChange={() => handleVersionToggle(version.versionId)}
                      disabled={isSaving}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant='body1'
                        sx={{ fontWeight: 500 }}
                      >
                        {version.version}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: neutral[600] }}
                      >
                        {version.accreditationBodyName}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </CommonLayout>
  );
}
